import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Megaphone, Plus, Activity, Building2, CheckCircle2, PauseCircle, Search, X,
  TrendingUp, AlertTriangle, ShieldAlert, BarChart2,
} from 'lucide-react';
import { api } from '@/lib/api';
import { EmptyState } from '@/components/ui/EmptyState';
import { StatusBadge } from '@/components/ui/Badge';
import { PageSkeleton } from '@/components/ui/Skeleton';
import { useAgencyProfitability, useAgencyChurnRisk, useAgencySla } from '@/hooks/useInsights';
import { useRealtimeEvents } from '@/hooks/useRealtimeEvents';
import { formatCurrency } from '@/lib/utils';
import type { Account } from '@/lib/api-types';

/**
 * Agency dashboard — entry point after login.
 *
 * Intentionally small: just a summary of the account portfolio and a jump-off
 * to individual account pages. All per-account KPI + charts live inside
 * ``AccountDetailPage`` where a single ``customer_id`` is in scope. Rolling
 * "agency-wide cost/clicks/conv" numbers would require a Laravel endpoint
 * that fans out to every worker — that work is tracked for Phase 2.
 */
type SortKey = 'name_asc' | 'name_desc' | 'recent' | 'status';

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<SortKey>('name_asc');

  // rt_ — subscribe to agency realtime events; invalidates query cache
  // for `accounts`, `mutations`, `findings` on relevant pushes.
  useRealtimeEvents();

  const { data: accounts = [], isLoading } = useQuery<Account[]>({
    queryKey: ['accounts'],
    queryFn: async () => (await api.get('/accounts')).data.data,
  });

  const { data: profitability } = useAgencyProfitability();
  const { data: churnRisk } = useAgencyChurnRisk();

  const { data: slaData } = useAgencySla();

  const filteredAccounts = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = accounts.filter((a) => {
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (!q) return true;
      return (
        a.name.toLowerCase().includes(q) ||
        a.alias?.toLowerCase().includes(q) ||
        a.customer_id?.toLowerCase().includes(q)
      );
    });
    const sorted = list.slice();
    switch (sortKey) {
      case 'name_asc':
        sorted.sort((a, b) => a.name.localeCompare(b.name, 'pl'));
        break;
      case 'name_desc':
        sorted.sort((a, b) => b.name.localeCompare(a.name, 'pl'));
        break;
      case 'recent':
        sorted.sort((a, b) => {
          const ta = a.last_sync_at ? Date.parse(a.last_sync_at) : 0;
          const tb = b.last_sync_at ? Date.parse(b.last_sync_at) : 0;
          return tb - ta;
        });
        break;
      case 'status':
        sorted.sort((a, b) => (a.status ?? '').localeCompare(b.status ?? ''));
        break;
    }
    return sorted;
  }, [accounts, query, statusFilter, sortKey]);

  if (isLoading) return <PageSkeleton />;

  if (accounts.length === 0) {
    return (
      <div className="container py-16">
        <EmptyState
          icon={<Megaphone size={48} />}
          title="Witaj w AIAds"
          description="Dodaj pierwsze konto Google Ads żeby zobaczyć statystyki, diagnostykę i AI copilota."
          action={
            <button className="btn-primary flex items-center gap-2" onClick={() => navigate('/accounts/new')}>
              <Plus size={16} />
              Dodaj konto
            </button>
          }
        />
      </div>
    );
  }

  // Summary stats from the fetched list (no backend call, no fake numbers).
  const activeCount = accounts.filter((a) => a.status === 'active').length;
  const pausedCount = accounts.filter((a) => a.status === 'paused').length;
  const archivedCount = accounts.filter((a) => a.status === 'archived').length;

  return (
    <div className="container py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">{t('nav.dashboard')}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {accounts.length} {accounts.length === 1 ? 'konto' : 'kont'} w agencji
          </p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => navigate('/accounts/new')}>
          <Plus size={16} />
          {t('accounts.add')}
        </button>
      </div>

      {/* Summary cards (portfolio totals, computed locally) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="card p-5 flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center text-brand-700">
            <Building2 size={20} />
          </div>
          <div>
            <p className="text-2xl font-semibold dark:text-white">{accounts.length}</p>
            <p className="text-xs text-slate-500">Wszystkie konta</p>
          </div>
        </div>
        <div className="card p-5 flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600">
            <CheckCircle2 size={20} />
          </div>
          <div>
            <p className="text-2xl font-semibold dark:text-white">{activeCount}</p>
            <p className="text-xs text-slate-500">Aktywne</p>
          </div>
        </div>
        <div className="card p-5 flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600">
            <PauseCircle size={20} />
          </div>
          <div>
            <p className="text-2xl font-semibold dark:text-white">{pausedCount}</p>
            <p className="text-xs text-slate-500">Wstrzymane</p>
          </div>
        </div>
        <div className="card p-5 flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500">
            <Activity size={20} />
          </div>
          <div>
            <p className="text-2xl font-semibold dark:text-white">{archivedCount}</p>
            <p className="text-xs text-slate-500">Zarchiwizowane</p>
          </div>
        </div>
      </div>

      {/* Agency overview row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="card p-5 flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600">
            <TrendingUp size={20} />
          </div>
          <div>
            <p className="text-2xl font-semibold dark:text-white">
              {profitability ? formatCurrency(profitability.profit, 'PLN') : '—'}
            </p>
            <p className="text-xs text-slate-500">{t('insights.agency_metrics.profit_ytd')}</p>
          </div>
        </div>

        <div className="card p-5 flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-500">
            <ShieldAlert size={20} />
          </div>
          <div>
            <p className="text-2xl font-semibold dark:text-white">
              {churnRisk
                ? churnRisk.accounts.filter((a) => a.risk_level === 'high' || a.risk_level === 'critical').length
                : '—'}
            </p>
            <p className="text-xs text-slate-500">{t('insights.agency_metrics.accounts_at_risk')}</p>
          </div>
        </div>

        <div className="card p-5 flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600">
            <AlertTriangle size={20} />
          </div>
          <div>
            <p className="text-2xl font-semibold dark:text-white">
              {slaData ? slaData.unresolved_count ?? 0 : '—'}
            </p>
            <p className="text-xs text-slate-500">{t('insights.agency_metrics.top_anomalies')}</p>
          </div>
        </div>

        <div
          className="card p-5 flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/agency/metrics')}
        >
          <div className="h-10 w-10 rounded-lg bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center text-brand-700">
            <BarChart2 size={20} />
          </div>
          <div>
            <p className="text-sm font-semibold dark:text-white">{t('nav.agency_metrics', { defaultValue: 'Metryki agencji' })}</p>
            <p className="text-xs text-slate-500">{t('insights.agency_metrics.view_all')}</p>
          </div>
        </div>
      </div>

      {/* Account list */}
      <div className="card">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-sm font-semibold shrink-0">{t('accounts.title')}</h3>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 w-full sm:w-auto sm:ml-auto">
            <div className="relative flex-1 sm:w-64">
              <Search
                size={14}
                aria-hidden="true"
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Szukaj konta, aliasu, customer ID…"
                aria-label="Szukaj konta, aliasu lub customer ID"
                className="input pl-8 pr-8 h-9 w-full text-sm"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label="Wyczyść"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <select
              className="select h-9 text-sm w-full sm:w-36"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filtruj wg statusu"
            >
              <option value="all">Każdy status</option>
              <option value="active">Aktywne</option>
              <option value="paused">Wstrzymane</option>
              <option value="archived">Zarchiwizowane</option>
            </select>
            <select
              className="select h-9 text-sm w-full sm:w-44"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              aria-label="Sortuj konta"
            >
              <option value="name_asc">Nazwa A → Z</option>
              <option value="name_desc">Nazwa Z → A</option>
              <option value="recent">Ostatnio synchronizowane</option>
              <option value="status">Wg statusu</option>
            </select>
            <button
              onClick={() => navigate('/accounts')}
              className="text-xs text-brand-600 hover:underline whitespace-nowrap"
            >
              Pokaż wszystkie
            </button>
          </div>
        </div>
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {filteredAccounts.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              {query || statusFilter !== 'all'
                ? 'Brak kont spełniających kryteria wyszukiwania.'
                : 'Brak kont do wyświetlenia.'}
            </div>
          ) : (
            filteredAccounts.map((a) => (
              <div
                key={a.id}
                className="p-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/30 cursor-pointer transition"
                onClick={() => navigate(`/accounts/${a.id}`)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-8 w-8 rounded-md bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center text-brand-700 text-xs font-bold shrink-0">
                    {a.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{a.name}</p>
                    <p className="text-[11px] text-slate-400 font-mono truncate">
                      {a.alias}
                      {a.customer_id ? ` · ${a.customer_id}` : ''}
                    </p>
                  </div>
                </div>
                <StatusBadge status={a.status} />
              </div>
            ))
          )}
        </div>
        <div className="p-2 border-t border-slate-100 dark:border-slate-800 text-[11px] text-slate-400 text-right">
          {filteredAccounts.length} / {accounts.length}
        </div>
      </div>
    </div>
  );
}
